import React, { useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import moment from 'moment';
import { View } from 'react-big-calendar';
import CalendarToolbar from './CalendarToolbar';
import ModelsCalendar from './ModelsCalendar';
import { ModelsCalendarProps } from './ModelsCalendar.types';
import {
  CALENDAR_DATE_FORMAT,
  CALENDAR_DATE_KEY,
  CALENDAR_VIEW_KEY,
  CALENDAR_VIEWS,
  DEFAULT_CALENDAR_VIEW,
} from '../../common/constants/calendar';

type ModelsCalendarWithToolbarProps = Omit<
  ModelsCalendarProps,
  'hideToolbar' | 'controlledDate' | 'controlledView' | 'onDateChange' | 'onViewChange'
>;

const ModelsCalendarWithToolbar = (props: ModelsCalendarWithToolbarProps) => {
  // Calendar toolbar state
  const [calendarDate, setCalendarDate] = useState<Date>(() => {
    if (globalThis.window) {
      const savedDate = globalThis.window.localStorage.getItem(CALENDAR_DATE_KEY);
      if (savedDate) {
        const date = moment(savedDate, CALENDAR_DATE_FORMAT);
        if (date.isValid()) {
          return date.toDate();
        }
      }
    }
    return new Date();
  });

  const [calendarView, setCalendarView] = useState<View>(() => {
    if (globalThis.window) {
      const savedView = globalThis.window.localStorage.getItem(CALENDAR_VIEW_KEY) as View;
      if (savedView && CALENDAR_VIEWS.includes(savedView as typeof CALENDAR_VIEWS[number])) {
        return savedView;
      }
    }
    return DEFAULT_CALENDAR_VIEW;
  });

  // Save calendar date to localStorage when it changes
  useEffect(() => {
    if (globalThis.window) {
      globalThis.window.localStorage.setItem(CALENDAR_DATE_KEY, moment(calendarDate).format(CALENDAR_DATE_FORMAT));
    }
  }, [calendarDate]);

  // Save calendar view to localStorage when it changes
  useEffect(() => {
    if (globalThis.window) {
      if (calendarView === DEFAULT_CALENDAR_VIEW) {
        globalThis.window.localStorage.removeItem(CALENDAR_VIEW_KEY);
      } else {
        globalThis.window.localStorage.setItem(CALENDAR_VIEW_KEY, calendarView);
      }
    }
  }, [calendarView]);

  const handleCalendarNavigate = useCallback((action: 'PREV' | 'NEXT' | 'TODAY') => {
    setCalendarDate(prevDate => {
      if (action === 'TODAY') {
        return new Date();
      }
      const unit = calendarView === 'month' ? 'month' : 'week';
      if (action === 'PREV') {
        return moment(prevDate).subtract(1, unit).toDate();
      }
      return moment(prevDate).add(1, unit).toDate();
    });
  }, [calendarView]);

  const handleCalendarViewChange = useCallback((newView: View) => {
    setCalendarView(newView);
  }, []);

  return (
    <>
      <CalendarToolbar
        date={calendarDate}
        view={calendarView}
        onNavigate={handleCalendarNavigate}
        onViewChange={handleCalendarViewChange}
        views={[...CALENDAR_VIEWS]}
      />
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <ModelsCalendar
          {...props}
          hideToolbar
          controlledDate={calendarDate}
          controlledView={calendarView}
          onDateChange={setCalendarDate}
          onViewChange={handleCalendarViewChange}
        />
      </Box>
    </>
  );
};

export default ModelsCalendarWithToolbar;
